import { useState, useEffect, useCallback, useMemo } from 'react';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { getProgramStatus } from '@shared/utilities';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import { sortPrograms } from '@/pages/RetailerOverview/helpers';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

interface UseProgramPeriodSelectorParams {
  rewardsPrograms?: ApiRewardsProgram[];
}

const selectInitialProgram = (
  programs: ApiRewardsProgram[] | undefined,
): ApiRewardsProgram | undefined => {
  if (!programs?.length) return undefined;
  const activeProgram = programs.find(
    (program) => getProgramStatus(program) === ProgramStatus.Active,
  );
  if (activeProgram) return activeProgram;
  const sorted = sortPrograms(programs);
  const expiredPrograms = sorted.filter(
    (program) => getProgramStatus(program) === ProgramStatus.Expired,
  );
  return expiredPrograms[expiredPrograms.length - 1];
};

export const useProgramPeriodSelector = (
  { rewardsPrograms: allRewardsPrograms }: UseProgramPeriodSelectorParams,
) => {
  const rewardsPrograms = useMemo(
    () => agronomyProgramsOnly(allRewardsPrograms),
    [allRewardsPrograms],
  );
  const sortedPrograms = useMemo(() => sortPrograms(rewardsPrograms ?? []), [rewardsPrograms]);

  const [selectedProgramPeriod, setSelectedProgramPeriod] = useState<ApiRewardsProgram | undefined>(
    () => selectInitialProgram(rewardsPrograms),
  );

  const getInitiallySelectedProgram = useCallback(
    (): ApiRewardsProgram | undefined => selectInitialProgram(rewardsPrograms),
    [rewardsPrograms],
  );

  useEffect(() => {
    if (rewardsPrograms?.length) {
      if (selectedProgramPeriod) {
        const hasRewardsProgram = rewardsPrograms.some(
          (program) => program.id === selectedProgramPeriod.id,
        );
        if (!hasRewardsProgram) {
          setSelectedProgramPeriod(getInitiallySelectedProgram());
        }
      } else {
        setSelectedProgramPeriod(getInitiallySelectedProgram());
      }
    }
  }, [rewardsPrograms, selectedProgramPeriod, getInitiallySelectedProgram]);

  return {
    selectedProgramPeriod,
    setSelectedProgramPeriod,
    sortedPrograms,
  };
};
